import {action, configure, makeObservable, observable} from "mobx";
import {router} from "@inertiajs/react";

export default class CatalogStore {
    /**
     * Количество товаров
     * @type {number}
     */
    @observable catalogCount = 0;

    /**
     * Сортировка (Имя по умолчанию === 'По популярности')
     * @type {string}
     */
    @observable selectedSortingName = 'По популярности';

    /**
     * Сортировка (Значение по умолчанию === 'hit')
     * @type {string}
     */
    @observable selectedSortingValue = 'hit';

    /**
     * Фильтры
     * @type {Map<any, any>}
     */
    filtersMap = new Map();

    /**
     * Активная страница, используется на фронте (пагинация)
     * @type {number}
     */
    @observable activePage = 1;

    /**
     * Активная страница, используется для запроса на сервер (пагинация)
     * @type {number}
     */
    @observable page = 1;

    /**
     * Сколько показывать товаров на странице (пагинация)
     * @type {number}
     */
    @observable perPage = 40;

    /**
     * Сколько показывать товаров на странице, неизменяемое значение (default)
     * @type {number}
     */
    perPageStart = 40;

    /**
     * Используется для кнопки "Показать еще" : "Загрузка..."
     * @type {boolean}
     */
    @observable loaderShowBtn = false;

    constructor(rootStore) {
        makeObservable(this);
        configure({
            enforceActions: "never",
        });
        this.rootStore = rootStore;
    }

    /**
     * Запрос данных по каталогу
     */
    @action getCatalogData = (preserveScroll = false) => {
        router.post(
            '/catalog' + this.getUri(),
            {
                page: this.page,
                perPage: this.perPage,
                sort: this.selectedSortingValue
            },
            {
                preserveScroll: preserveScroll,
                onSuccess: () => {
                    this.loaderShowBtn = false;
                }
            }
        );
    };

    /**
     * Заполнение и обновление данных
     * @param props
     */
    start = (props) => {
        const {count, catalogSort = '', dataSort = [], selectedFilters = []} = props;

        // Сортировка
        this.selectedSortingValue = catalogSort;
        this.selectedSortingName = dataSort.find(item => item.value === catalogSort)?.label ?? dataSort[0].label;

        // Количество найденных товаров
        this.catalogCount = count;
    };

    /**
     * Только заполнение данных при старте каталога
     * @param props
     */
    startFilters = (props) => {
        const {page = 1, perPage = 40, perPageStart = 40, selectedFilters = []} = props;
        if (perPage !== this.perPage) {
            this.activePage = page;
        }
        this.page = page;
        this.perPage = perPage;
        this.perPageStart = perPageStart;

        // Заполнение фильтров
        this.deleteFilters();
        selectedFilters.forEach((el) => {
            this.addOrRemoveFilter(el);
        });
    }

    /**
     * Добавить или удалить данные по фильтрам
     * @param filter Конфигурация одного фильтра
     * @param dependencies Список ключей зависимых фильтров, которые нужно сбросить при удаление фильтра
     */
    addOrRemoveFilter = (filter, dependencies = []) => {
        const {
            indexUrl,      // Индекс позиции фильтра в URI (для сортировки)
            key,           // Уникальный ключ фильтра, например 'category' или 'inserts'
            type,          // Тип фильтра — 'radio' или 'checked'
            valueAlias,    // Значение внутри фильтра, например 'cepi' или 'agat'
        } = filter;

        // Если фильтр по ключу ещё не добавлен — создаём его с пустым набором значений
        if (!this.filtersMap.has(key)) {
            this.filtersMap.set(key, {
                indexUrl,
                type,
                values: new Set() // Set — для удобного добавления/удаления без дубликатов
            });
        }

        // Получаем текущую запись фильтра из карты
        const entry = this.filtersMap.get(key);

        // Обновляем indexUrl и type (на случай, если они изменились)
        entry.indexUrl = indexUrl;
        entry.type = type;

        // Проверяем, выбрано ли уже это значение
        const isAlreadySelected = entry.values.has(valueAlias);

        if (type === 'range') {
            entry.values = filter.valueAlias === null ? new Set() : new Set([valueAlias]);
        } else if (type === 'radio') {
            // Радио-кнопка (можно выбрать только одно значение), удаляем значение, (если оно уже было выбрано, снимаем выбор) или устанавливаем новое значение (одно единственное)
            entry.values = isAlreadySelected ? new Set() : new Set([valueAlias]);
        } else {
            // Чекбокс (множественный фильтр), удаляем значение, если оно уже было выбрано или добавляем значение, если его ещё нет
            isAlreadySelected ? entry.values.delete(valueAlias) : entry.values.add(valueAlias);
        }

        // Проверка на наличие зависимых фильтров
        // const dependentKeys = this.filterDependencies[key];
        if (Array.isArray(dependencies)) {
            for (const depKey of dependencies) {
                this.filtersMap.delete(depKey);
            }
        }

        // Если после действия значения пустые — удаляем весь фильтр
        if (entry.values.size === 0) {
            this.filtersMap.delete(key);
        }
    };

    /**
     * Собираем uri на основе выбранных фильтров
     * @returns {string}
     */
    getUri = () => {
        if (this.filtersMap.size === 0) return '';

        const sorted = Array.from(this.filtersMap.entries())
            .map(([key, entry]) => ({
                key,
                indexUrl: entry.indexUrl,
                values: Array.from(entry.values).sort()
            }))
            .sort((a, b) => a.indexUrl - b.indexUrl);

        const parts = sorted.map(({values}) => values.join(','));

        return '/' + parts.join('/');
    }

    /**
     * Обработка потери фокуса
     * @param newRange
     * @param el
     */
    @action onBlur = (newRange, el) => {
        try {
            const valueAlias = `${el.key}-min-${newRange.value_min},${el.key}-max-${newRange.value_max}`;

            this.onChangeFilter({...el, valueAlias: valueAlias});
        } catch (e) {
            console.log('e', e);
        }
    };

    /**
     * Используется в фильтрах, общий метод (обработка radio, checkbox, range)
     * @param filter Конфигурация одного фильтра
     * @param dependencies Список ключей зависимых фильтров, которые нужно сбросить при удаление фильтра
     */
    @action onChangeFilter = (filter, dependencies = []) => {
        this.addOrRemoveFilter(filter, dependencies);
        this.changePagination();
        this.getCatalogData(false);
    }

    /**
     * Удаление одного выбранного фильтра
     */
    @action deleteFilter = (filter) => {
        if (filter.type === 'range') {
            filter.valueAlias = null;
        }
        this.addOrRemoveFilter(filter, filter.dependencies);
        this.changePagination();
        this.getCatalogData(false);
    };

    /**
     * Сбросить все выбранные фильтры
     */
    @action deleteFilters = () => {
        this.filtersMap = new Map();
    }

    /**
     * Сбросить все по фильтрации
     */
    @action resetAllFilters = () => {
        this.deleteFilters();
        this.changePagination();
        this.getCatalogData(false);
    }

    /**
     * Сортировка, сценарий
     * @param option
     */
    @action applySorting = (option) => {
        this.changeSorting(option);
        this.changePagination();
        this.getCatalogData(false);
    }

    /**
     * Сортировка
     * @param option
     */
    @action changeSorting = (option) => {
        this.selectedSortingName = option.label;
        this.selectedSortingValue = option.value;
    }

    /**
     * Сбрасываем/Переключаем пагинацию
     * @param pageNumber
     */
    @action changePagination = (pageNumber = 1) => {
        this.activePage = pageNumber;
        this.page = pageNumber;
        this.perPage = this.perPageStart;
    }

    /**
     * Пагинация
     * @param pageNumber
     */
    @action handlePageChange = (pageNumber) => {
        this.changePagination(pageNumber);
        this.getCatalogData(false);
    };

    /**
     * Показать еще
     */
    @action showMore = () => {
        if (this.loaderShowBtn) return;
        this.loaderShowBtn = true;
        this.activePage += 1;
        this.perPage += this.perPageStart;
        this.getCatalogData(true);
    };
}
